import {
  FALLBACK_LOCALES,
  MOMENT_LOCALES_MAPPING,
  SUPPORTED_LOCALES,
  localeLanguagePart,
  localeLastPart,
} from 'config/locales'
import moment, { Moment } from 'moment'

import { observer } from 'mobx-react'

import SelectField from 'modules/core/components/SelectField'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import type PersonalForm from 'modules/settings/lib/forms/Personal'

import { useWithoutDisabledLocales } from './LanguageSelector'

interface Props {
  form: PersonalForm
}

const getLabel = (date: Moment, locale: string): string => {
  date.locale(MOMENT_LOCALES_MAPPING[locale] || locale)

  return `${localeLastPart(locale).toUpperCase()} (${date.format('L')} - ${formatCurrency(
    123456,
    locale
  )})`
}

const FormatsSelector = ({ form }: Props) => {
  const localeField = form.get('locale')
  const currentLocale: string = localeField.value.toLowerCase()
  const languageFromLocale = localeLanguagePart(currentLocale)

  const possibleLocales = [
    // Current value (e.g. es-ar -> es-ar)
    currentLocale,
    // Default locale for the current language (e.g. en -> en-us)
    FALLBACK_LOCALES[languageFromLocale],
    // Locale without region code (e.g. es-es -> es)
    languageFromLocale,
  ]

  const supportedLocales = useWithoutDisabledLocales(SUPPORTED_LOCALES)
  const selectedLocale = possibleLocales.find(
    (locale) => locale && supportedLocales.includes(locale)
  )

  // If the locale is not found for the current language, we show a "Select" option
  const missingLocale = selectedLocale ? {} : { none: i18n.t('common.select') }

  const date = moment().month(3).date(30) // 30th of April, a good date to show the format
  const options = supportedLocales
    .filter((locale) => locale.startsWith(languageFromLocale))
    .map((locale) => ({
      value: locale,
      label: getLabel(date, locale),
    }))

  return (
    <SelectField
      label={i18n.t('personal_settings.date_number_format')}
      options={options}
      value={selectedLocale}
      onChange={(value) => localeField.set(value)}
      showOriginalLabelText
      mandatory
      {...missingLocale}
    />
  )
}

export default observer(FormatsSelector)
